import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Info, Pencil, Send, X } from 'lucide-react';
import { useState } from 'react';
import type { SubscriberListRef } from '@/features/subscribers';
import { pluralise } from '@/shared/lib/format';
import type { Draft } from '../api/draftsApi';
import { DecidedFields, PendingFields } from './DraftFields';
import { DraftHeader } from './DraftHeader';
import { MessageEditor } from './MessageEditor';
import { enterSx, outlineButtonSx } from './styles';
import { WhatsAppPreview } from './WhatsAppPreview';

type Props = {
  draft: Draft;
  lists: SubscriberListRef[];
  canDecide: boolean;
  role: string;
  isBusy: boolean;
  onApprove: (text: string, listId: string) => void;
  onReject: () => void;
};

const TIME = new Intl.DateTimeFormat('en-SG', { hour: 'numeric', minute: '2-digit' });

const sentTime = (d: Draft) => (d.decidedAt && d.status !== 'rejected' ? TIME.format(new Date(d.decidedAt)) : 'now');

type BarProps = { listName: string; count: number; isBusy: boolean; isEditing: boolean; onEdit: () => void; onReject: () => void; onApprove: () => void };

function ActionBar({ listName, count, isBusy, isEditing, onEdit, onReject, onApprove }: BarProps) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', px: { xs: 2, md: 2.5 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Button variant="outlined" color="error" startIcon={<X size={14} />} disabled={isBusy} onClick={onReject}>Reject</Button>
      <Button variant="outlined" startIcon={<Pencil size={14} />} disabled={isBusy || isEditing} onClick={onEdit} sx={outlineButtonSx}>Edit message</Button>
      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>Sends to {pluralise(count, 'number')} in {listName}</Typography>
      <Button variant="contained" endIcon={<Send size={14} />} disabled={isBusy || isEditing || count === 0} onClick={onApprove}>Approve and send</Button>
    </Stack>
  );
}

/** An analyst's actions, or a note for anyone else. */
function Footer({ canDecide, role, bar }: { canDecide: boolean; role: string; bar: BarProps }) {
  if (canDecide) return <ActionBar {...bar} />;
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider', color: 'text.secondary' }}>
      <Info size={14} aria-hidden />
      <Typography variant="caption">Research analysts approve messages. You&apos;re viewing this as {role === 'admin' ? 'an admin' : 'an RM'}.</Typography>
    </Stack>
  );
}

/** One message in full. While it waits, an analyst can change the list, edit the text, then approve or reject. */
export function DraftDetail({ draft, lists, canDecide, role, isBusy, onApprove, onReject }: Props) {
  const [text, setText] = useState(draft.finalText);
  const [listId, setListId] = useState(draft.list.id);
  const [isEditing, setEditing] = useState(false);
  const isPending = draft.status === 'pending';
  const list = lists.find((l) => l.id === listId);
  const time = sentTime(draft);

  return (
    <Stack component="article" aria-labelledby="draft-title" sx={{ height: '100%' }}>
      <Stack key={draft.id} sx={{ ...enterSx, gap: 2.5, p: { xs: 2, md: 2.5 }, flexGrow: 1 }}>
        <DraftHeader draft={draft} />
        {isPending ? <PendingFields draft={draft} lists={lists} listId={listId} onList={setListId} canDecide={canDecide} /> : <DecidedFields draft={draft} />}
        {isEditing ? (
          <MessageEditor text={text} onCancel={() => { setEditing(false); }} onSave={(t) => { setText(t); setEditing(false); }} />
        ) : (
          <WhatsAppPreview text={text} disclaimer={draft.disclaimer} time={time} />
        )}
      </Stack>
      {isPending ? (
        <Footer
          canDecide={canDecide}
          role={role}
          bar={{ listName: list?.name ?? draft.list.name, count: list?.optedInCount ?? draft.list.count, isBusy, isEditing, onEdit: () => { setEditing(true); }, onReject, onApprove: () => { onApprove(text.trim(), listId); } }}
        />
      ) : null}
    </Stack>
  );
}
